import React, { type ReactNode } from 'react';
import { styled } from '@compiled/react';
import { DropdownItem } from '@atlaskit/dropdown-menu';
import { colors } from '@atlaskit/theme';
import { fontFallback } from '@atlaskit/theme/typography';
import { token } from '@atlaskit/tokens';
import Tooltip, { type PositionType } from '@atlaskit/tooltip';

type Props = {
	name: string;
	icon: ReactNode;
	description?: string;
	onClick?: () => void;
	isDisabled?: boolean;
	isSelected?: boolean;
	tooltipContent?: string;
	tooltipPosition?: PositionType;
};

export const FieldSelectorItem = ({
	name,
	description,
	icon,
	isSelected,
	isDisabled,
	tooltipContent,
	tooltipPosition = 'right',
	onClick,
}: Props) => {
	const content = (
		<>
			<DropdownItemHeading>{name}</DropdownItemHeading>
			{description && (
				<DropdownItemDescription isSelected={isSelected}>{description}</DropdownItemDescription>
			)}
		</>
	);

	if (isSelected) {
		return (
			<SelectedFieldWrapper data-testId="project-create-importer-upload-flow.common.ui.field-selector-item.selected-field-wrapper">
				{icon}
				<SelectedContentWrapper>{content}</SelectedContentWrapper>
			</SelectedFieldWrapper>
		);
	}

	if (isDisabled) {
		return (
			<Tooltip content={tooltipContent} position={tooltipPosition} ignoreTooltipPointerEvents>
				<StyledDropdownItem onClick={onClick} elemBefore={icon} isDisabled>
					<ContentWrapper>{content}</ContentWrapper>
				</StyledDropdownItem>
			</Tooltip>
		);
	}

	return (
		<StyledDropdownItem onClick={onClick} elemBefore={icon}>
			<ContentWrapper>{content}</ContentWrapper>
		</StyledDropdownItem>
	);
};

// Needed to match dropdown item styling
const customPadding = `${token('space.100', '8px')} ${token('space.200', '16px')} ${token(
	'space.100',
	'8px',
)} 14px`;

// eslint-disable-next-line @atlaskit/ui-styling-standard/no-styled -- Ignored via go/DSP-18766
const StyledDropdownItem = styled(DropdownItem)({
	// eslint-disable-next-line @atlaskit/ui-styling-standard/no-unsafe-selectors -- Ignored via go/DSP-18766
	':hover:not([disabled])': {
		// eslint-disable-next-line @atlaskit/ui-styling-standard/no-imported-style-values -- Ignored via go/DSP-18766
		borderLeft: `${token('space.025', '2px')} solid ${token('color.text.selected', colors.B400)}`,
		padding: customPadding,
	},
});

// eslint-disable-next-line @atlaskit/ui-styling-standard/no-styled -- To migrate as part of go/ui-styling-standard
const SelectedFieldWrapper = styled.div({
	display: 'flex',
	flexDirection: 'row',
	alignItems: 'center',
	cursor: 'default',
	padding: customPadding,
	// eslint-disable-next-line @atlaskit/ui-styling-standard/no-imported-style-values -- Ignored via go/DSP-18766
	backgroundColor: token('color.background.accent.blue.subtlest', colors.B50),
	// eslint-disable-next-line @atlaskit/ui-styling-standard/no-imported-style-values -- Ignored via go/DSP-18766
	color: token('color.text.selected', colors.B400),
	// eslint-disable-next-line @atlaskit/ui-styling-standard/no-imported-style-values -- Ignored via go/DSP-18766
	borderLeft: `${token('space.025', '2px')} solid ${token('color.text.selected', colors.B400)}`,
});

// eslint-disable-next-line @atlaskit/ui-styling-standard/no-styled -- To migrate as part of go/ui-styling-standard
const SelectedContentWrapper = styled.div({
	display: 'flex',
	flexDirection: 'column',
	marginLeft: token('space.150', '12px'),
});

// eslint-disable-next-line @atlaskit/ui-styling-standard/no-styled -- To migrate as part of go/ui-styling-standard
const ContentWrapper = styled.div({
	display: 'flex',
	flexDirection: 'column',
});

type DropdownItemDescriptionProps = {
	isSelected?: boolean;
};

// eslint-disable-next-line @atlaskit/ui-styling-standard/no-styled -- To migrate as part of go/ui-styling-standard
const DropdownItemDescription = styled.span<DropdownItemDescriptionProps>({
	// eslint-disable-next-line @atlaskit/ui-styling-standard/no-imported-style-values -- Ignored via go/DSP-18766
	font: token('font.body.UNSAFE_small', fontFallback.body.UNSAFE_small),
	// eslint-disable-next-line @atlaskit/ui-styling-standard/no-dynamic-styles -- Ignored via go/DSP-18766
	color: `${({ isSelected }: DropdownItemDescriptionProps) =>
		isSelected
			? // eslint-disable-next-line @atlaskit/ui-styling-standard/no-imported-style-values -- Ignored via go/DSP-18766
				token('color.text.selected', colors.B400)
			: // eslint-disable-next-line @atlaskit/ui-styling-standard/no-imported-style-values -- Ignored via go/DSP-18766
				token('color.text.subtlest', colors.N200)}`,
});

// eslint-disable-next-line @atlaskit/ui-styling-standard/no-styled -- To migrate as part of go/ui-styling-standard
const DropdownItemHeading = styled.span({
	// eslint-disable-next-line @atlaskit/ui-styling-standard/no-imported-style-values -- Ignored via go/DSP-18766
	font: token('font.body', fontFallback.body.medium),
});
